/**
 * The reference shown to the user, derived from the error rather than generated.
 *
 * It must never change under someone who is mid-way through copying it, which rules out
 * anything random: an error boundary can remount (a TanStack retry re-throwing is enough)
 * and a fresh random value would replace the one already on screen. Deriving it from the
 * error is stable across remounts, retries and hydration, and is available on the very
 * first render - so there is no window where the line is missing or wrong.
 *
 * Same idea as Next's own `digest`, which is a hash of the error; when a digest exists we
 * use it directly so the UI, the server log and PostHog all quote one string.
 *
 * Consequence worth knowing: the same failure always yields the same reference, so a
 * reference identifies an error, not one occurrence of it. Support should read the most
 * recent matching event.
 */
export const referenceFor = (error: Error & { digest?: string }) =>
  error.digest ?? fnv1a(`${error.name}:${error.message}`);

// FNV-1a, 32-bit. Chosen for being tiny and dependency-free - this runs on the fatal
// screen, which must not pull in anything.
const fnv1a = (input: string) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};
